import { useEffect, useRef, useState } from 'react';
import { registerSW } from 'virtual:pwa-register';

type Status = 'idle' | 'caching' | 'ready' | 'error';

interface Options {
  timeoutId?: string;
  timeoutMs?: number;
}

export function usePWAStatus({ timeoutId, timeoutMs = 120000 }: Options = {}) {
  const [status, setStatus] = useState<Status>('idle');
  const timeoutRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    if (import.meta.env.DEV) {
      setStatus('ready');
      return;
    }

    // If a SW already controls the page, we're effectively "ready"
    if (navigator.serviceWorker?.controller) {
      setStatus((s) => (s === 'idle' ? 'ready' : s));
    }

    const onControllerChange = () => setStatus('ready');
    navigator.serviceWorker?.addEventListener(
      'controllerchange',
      onControllerChange,
    );

    registerSW({
      immediate: true,
      onRegisteredSW: (_url, reg) => {
        if (!reg) {
          setStatus('ready');
          return;
        }

        if (reg.installing) {
          setStatus('caching');
          reg.installing.addEventListener('statechange', () => {
            if (reg.installing?.state === 'installed') {
              setStatus('ready');
            }
          });
        } else {
          setStatus('ready');
        }
      },
      onOfflineReady: () => setStatus('ready'),
      onRegisterError: () => setStatus('error'),
    });

    // --- timeout logic
    if (timeoutId) {
      // clear any previous timer for this ID
      if (timeoutRef.current[timeoutId]) {
        clearTimeout(timeoutRef.current[timeoutId]);
      }

      timeoutRef.current[timeoutId] = setTimeout(() => {
        setStatus((prev) => (prev === 'ready' ? prev : 'ready'));
      }, timeoutMs);
    }

    return () => {
      navigator.serviceWorker?.removeEventListener(
        'controllerchange',
        onControllerChange,
      );

      // cleanup this instance’s timer only
      if (timeoutId && timeoutRef.current[timeoutId]) {
        clearTimeout(timeoutRef.current[timeoutId]);
        delete timeoutRef.current[timeoutId];
      }
    };
  }, [timeoutId, timeoutMs]);

  return { status };
}
